import { Shield, CheckCircle, TrendingUp, Activity, X, ChevronRight, MousePointer } from 'lucide-react';
import { ALL_THREATS } from '../data/threatData';

interface WelcomeModalProps {
  onClose: () => void;
}

const STEPS = [
  {
    icon: <MousePointer className="w-3.5 h-3.5 text-cyan-400" />,
    title: 'Browse 5 lifecycle phases',
    description:
      'Use the tabs — Proactive, Discovery, Collection, Exfiltration, Impact — to explore the full insider threat attack chain.',
  },
  {
    icon: <ChevronRight className="w-3.5 h-3.5 text-cyan-400" />,
    title: 'Click any card for full details',
    description:
      'Each threat card opens with a description, key indicators to watch for, and recommended mitigations.',
  },
  {
    icon: <CheckCircle className="w-3.5 h-3.5 text-cyan-400" />,
    title: 'Check off threats you\'re monitoring',
    description:
      'Toggle the icon on each card to add it to your active list. Checked threats drive your Risk Score and Coverage metrics.',
  },
  {
    icon: <Activity className="w-3.5 h-3.5 text-cyan-400" />,
    title: 'Risk Score updates in real time',
    description:
      'Your score is severity-weighted — critical threats count more than low-risk ones — giving you a proportional exposure view.',
  },
  {
    icon: <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />,
    title: 'Set your Maturity Level to find gaps',
    description:
      'Click 1–5 in the Maturity panel to benchmark your coverage against the expected threshold for each maturity stage.',
  },
];

const WelcomeModal = ({ onClose }: WelcomeModalProps) => (
  <div
    className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
    onClick={onClose}
  >
    <div
      className="bg-gray-900 border border-gray-700/80 rounded-2xl p-7 max-w-md w-full shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30 flex-shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white leading-tight">Insider Threat Matrix</h2>
            <p className="text-xs text-cyan-400 font-medium mt-0.5">2026 Edition · By CISO Marketplace</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-gray-800 transition-colors flex-shrink-0 ml-4"
          aria-label="Close welcome guide"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Description */}
      <p className="text-gray-300 text-sm leading-relaxed mb-5">
        An interactive framework mapping{' '}
        <span className="text-cyan-400 font-semibold">{ALL_THREATS.length} insider threat TTPs</span> across
        the full attack lifecycle. Browse free; build your personalised risk profile with credits, a Tools plan,
        or one-time access — included with <a href="https://microsec.tools/pricing" className="text-cyan-400 underline">CISO Marketplace membership</a>.
      </p>

      {/* Steps */}
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">How to use this tool</p>
      <div className="space-y-3 mb-6">
        {STEPS.map((step, i) => (
          <div key={i} className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mt-0.5">
              {step.icon}
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-snug">{step.title}</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[
          { value: ALL_THREATS.length, label: 'Threats mapped' },
          { value: 5, label: 'Lifecycle phases' },
          { value: 'Members-free', label: 'or credits / guest pass' },
        ].map(({ value, label }) => (
          <div
            key={label}
            className="bg-gray-800/60 rounded-xl p-2.5 text-center border border-gray-700/60"
          >
            <div className="text-lg font-bold text-cyan-400">{value}</div>
            <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">{label}</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={onClose}
        className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-cyan-500/20 flex items-center justify-center space-x-2 text-sm"
      >
        <span>Get Started</span>
        <ChevronRight className="w-4 h-4" />
      </button>
      <p className="text-center text-[11px] text-gray-600 mt-2.5">Won't show again on future visits</p>
    </div>
  </div>
);

export default WelcomeModal;
